import { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { logger } from './loggingService';
import { toast } from '../lib/toast';

type SubscriptionCallback<T = any> = (payload: RealtimePostgresChangesPayload<T>) => void;
type PresenceCallback = (state: any) => void;
type BroadcastCallback = (message: any) => void;

interface Subscription {
  id: string;
  channel: RealtimeChannel;
  table?: string;
  event?: string;
  filter?: string;
  callback: SubscriptionCallback | PresenceCallback | BroadcastCallback;
  type: 'postgres' | 'presence' | 'broadcast';
  createdAt: Date;
  errorCount: number;
  lastError?: Date;
}

interface SubscriptionOptions {
  table?: string;
  event?: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  filter?: string;
  schema?: string;
}

/**
 * Centralized manager for Supabase real-time subscriptions
 * Prevents memory leaks and handles reconnection logic
 */
export class SubscriptionManager {
  private static instance: SubscriptionManager | null = null;
  private subscriptions = new Map<string, Subscription>();
  private reconnectTimer: NodeJS.Timeout | null = null;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isOnline = true;
  private healthCheckInterval: NodeJS.Timeout | null = null;

  private constructor() {
    this.setupNetworkListeners();
    this.startHealthCheck();
    logger.info('SubscriptionManager initialized');
  }

  /**
   * Get singleton instance
   */
  static getInstance(): SubscriptionManager {
    if (!this.instance) {
      this.instance = new SubscriptionManager();
    }
    return this.instance;
  }

  /**
   * Subscribe to database changes
   */
  subscribeToTable<T = any>(
    options: SubscriptionOptions,
    callback: SubscriptionCallback<T>
  ): string {
    const { table, event = '*', filter, schema = 'public' } = options;
    
    if (!table) {
      throw new Error('Table name is required for database subscription');
    }

    const subscriptionId = this.generateId('postgres', table, event, filter);
    
    // Check if already subscribed
    if (this.subscriptions.has(subscriptionId)) {
      logger.warn('Subscription already exists', { subscriptionId });
      return subscriptionId;
    }

    try {
      // Create channel
      const channelName = `${schema}:${table}:${event}:${filter || 'all'}`;
      const channel = supabase.channel(channelName);

      // Setup subscription
      const subscription = channel.on<T>(
        'postgres_changes' as any,
        {
          event: event === '*' ? undefined : event,
          schema,
          table,
          filter
        },
        (payload) => {
          try {
            logger.debug('Database change received', { 
              table, 
              event: payload.eventType,
              id: (payload.new as any)?.id || (payload.old as any)?.id
            });
            callback(payload);
          } catch (error) {
            this.handleCallbackError(subscriptionId, error);
          }
        }
      );

      // Subscribe to channel
      subscription.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          logger.info('Subscribed to table', { table, event, filter });
        } else if (status === 'CHANNEL_ERROR') {
          logger.error('Subscription error', { table, status });
          this.handleSubscriptionError(subscriptionId);
        } else if (status === 'TIMED_OUT') {
          logger.warn('Subscription timeout', { table });
          this.reconnectSubscription(subscriptionId);
        }
      });

      // Store subscription
      this.subscriptions.set(subscriptionId, {
        id: subscriptionId,
        channel,
        table,
        event,
        filter,
        callback,
        type: 'postgres',
        createdAt: new Date(),
        errorCount: 0
      });

      return subscriptionId;
    } catch (error) {
      logger.error('Failed to create subscription', { error, table });
      throw error;
    }
  }

  /**
   * Subscribe to presence (user status)
   */
  subscribeToPresence(
    channelName: string,
    callback: PresenceCallback
  ): string {
    const subscriptionId = this.generateId('presence', channelName);
    
    if (this.subscriptions.has(subscriptionId)) {
      logger.warn('Presence subscription already exists', { subscriptionId });
      return subscriptionId;
    }

    try {
      const channel = supabase.channel(channelName);

      // Track presence
      channel
        .on('presence', { event: 'sync' }, () => {
          const state = channel.presenceState();
          try {
            callback(state);
          } catch (error) {
            this.handleCallbackError(subscriptionId, error);
          }
        })
        .on('presence', { event: 'join' }, ({ key, newPresences }) => {
          try {
            callback({ event: 'join', key, newPresences });
          } catch (error) {
            this.handleCallbackError(subscriptionId, error);
          }
        })
        .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
          try {
            callback({ event: 'leave', key, leftPresences });
          } catch (error) {
            this.handleCallbackError(subscriptionId, error);
          }
        })
        .subscribe(async (status) => {
          if (status === 'SUBSCRIBED') {
            // Send initial presence
            const userState = {
              user: (await supabase.auth.getUser()).data.user?.email,
              online_at: new Date().toISOString()
            };
            await channel.track(userState);
            logger.info('Subscribed to presence', { channelName });
          }
        });

      // Store subscription
      this.subscriptions.set(subscriptionId, {
        id: subscriptionId,
        channel,
        callback,
        type: 'presence',
        createdAt: new Date(),
        errorCount: 0
      });

      return subscriptionId;
    } catch (error) {
      logger.error('Failed to create presence subscription', { error, channelName });
      throw error;
    }
  }

  /**
   * Subscribe to broadcast messages
   */
  subscribeToBroadcast(
    channelName: string,
    event: string,
    callback: BroadcastCallback
  ): string {
    const subscriptionId = this.generateId('broadcast', channelName, event);
    
    if (this.subscriptions.has(subscriptionId)) {
      logger.warn('Broadcast subscription already exists', { subscriptionId });
      return subscriptionId;
    }

    try {
      const channel = supabase.channel(channelName);

      channel
        .on('broadcast', { event }, (payload) => {
          try {
            callback(payload);
          } catch (error) {
            this.handleCallbackError(subscriptionId, error);
          }
        })
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            logger.info('Subscribed to broadcast', { channelName, event });
          }
        });

      // Store subscription
      this.subscriptions.set(subscriptionId, {
        id: subscriptionId,
        channel,
        callback,
        type: 'broadcast',
        createdAt: new Date(),
        errorCount: 0
      });

      return subscriptionId;
    } catch (error) {
      logger.error('Failed to create broadcast subscription', { error, channelName });
      throw error;
    }
  }

  /**
   * Send broadcast message
   */
  async broadcast(channelName: string, event: string, payload: any): Promise<void> {
    try {
      const channel = supabase.channel(channelName);
      await channel.send({
        type: 'broadcast',
        event,
        payload
      });
      logger.debug('Broadcast sent', { channelName, event });
    } catch (error) {
      logger.error('Failed to send broadcast', { error, channelName, event });
      throw error;
    }
  }

  /**
   * Unsubscribe from a specific subscription
   */
  async unsubscribe(subscriptionId: string): Promise<void> {
    const subscription = this.subscriptions.get(subscriptionId);
    if (!subscription) {
      logger.warn('Subscription not found', { subscriptionId });
      return;
    }

    try {
      await subscription.channel.unsubscribe();
      this.subscriptions.delete(subscriptionId);
      logger.info('Unsubscribed', { subscriptionId });
    } catch (error) {
      logger.error('Failed to unsubscribe', { error, subscriptionId });
      throw error;
    }
  }

  /**
   * Unsubscribe from all subscriptions
   */
  async unsubscribeAll(): Promise<void> {
    const promises: Promise<void>[] = [];
    
    for (const [id] of this.subscriptions) {
      promises.push(this.unsubscribe(id));
    }

    await Promise.allSettled(promises);
    this.subscriptions.clear();
    logger.info('All subscriptions cleared');
  }

  /**
   * Pause all subscriptions (useful during auth changes)
   */
  async pauseAll(): Promise<void> {
    for (const [, subscription] of this.subscriptions) {
      try {
        await subscription.channel.unsubscribe();
      } catch (error) {
        logger.error('Failed to pause subscription', { error, id: subscription.id });
      }
    }
    logger.info('All subscriptions paused');
  }

  /**
   * Resume all subscriptions
   */
  async resumeAll(): Promise<void> {
    for (const [, subscription] of this.subscriptions) {
      try {
        await subscription.channel.subscribe();
      } catch (error) {
        logger.error('Failed to resume subscription', { error, id: subscription.id });
      }
    }
    logger.info('All subscriptions resumed');
  }

  /**
   * Handle subscription errors
   */
  private handleSubscriptionError(subscriptionId: string): void {
    const subscription = this.subscriptions.get(subscriptionId);
    if (!subscription) return;

    subscription.errorCount++;
    subscription.lastError = new Date();

    if (subscription.errorCount >= this.maxReconnectAttempts) {
      logger.error('Max reconnect attempts reached, removing subscription', { 
        subscriptionId 
      });
      this.unsubscribe(subscriptionId);
      toast.error('Real-time connection lost. Please refresh the page.');
    } else {
      this.reconnectSubscription(subscriptionId);
    }
  }

  /**
   * Handle callback errors
   */
  private handleCallbackError(subscriptionId: string, error: any): void {
    logger.error('Subscription callback error', { 
      subscriptionId, 
      error: error?.message || error 
    });
    
    // Don't remove subscription for callback errors
    // Let the component handle the error
  }

  /**
   * Reconnect a subscription
   */
  private async reconnectSubscription(subscriptionId: string): Promise<void> {
    const subscription = this.subscriptions.get(subscriptionId);
    if (!subscription) return;

    const delay = this.reconnectDelay * Math.pow(2, subscription.errorCount);
    
    logger.info('Reconnecting subscription', { 
      subscriptionId, 
      delay, 
      attempt: subscription.errorCount 
    });

    setTimeout(async () => {
      try {
        await subscription.channel.subscribe();
        subscription.errorCount = 0; // Reset on success
      } catch (error) {
        this.handleSubscriptionError(subscriptionId);
      }
    }, delay);
  }

  /**
   * Setup network change listeners
   */
  private setupNetworkListeners(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('online', () => {
      logger.info('Network online, resuming subscriptions');
      this.isOnline = true;
      this.resumeAll();
    });

    window.addEventListener('offline', () => {
      logger.warn('Network offline, pausing subscriptions');
      this.isOnline = false;
      this.pauseAll();
    });

    // Handle page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        logger.debug('Page hidden, pausing subscriptions');
        this.pauseAll();
      } else {
        logger.debug('Page visible, resuming subscriptions');
        this.resumeAll();
      }
    });
  }

  /**
   * Start health check for subscriptions
   */
  private startHealthCheck(): void {
    this.healthCheckInterval = setInterval(() => {
      if (!this.isOnline) return;

      for (const [id, subscription] of this.subscriptions) {
        const age = Date.now() - subscription.createdAt.getTime();
        const maxAge = 30 * 60 * 1000; // 30 minutes

        // Recreate old subscriptions
        if (age > maxAge) {
          logger.info('Recreating old subscription', { id, age });
          this.recreateSubscription(id);
        }
      }
    }, 60000); // Check every minute
  }

  /**
   * Recreate a subscription
   */
  private async recreateSubscription(subscriptionId: string): Promise<void> {
    const subscription = this.subscriptions.get(subscriptionId);
    if (!subscription) return;

    // Store original details
    const { callback, type, table, event, filter } = subscription;

    // Unsubscribe old
    await this.unsubscribe(subscriptionId);

    // Recreate based on type
    if (type === 'postgres' && table) {
      this.subscribeToTable(
        { table, event: event as any, filter },
        callback as SubscriptionCallback
      );
    }
  }

  /**
   * Generate unique subscription ID
   */
  private generateId(...parts: (string | undefined)[]): string {
    return parts.filter(Boolean).join(':');
  }

  /**
   * Get subscription statistics
   */
  getStats(): {
    total: number;
    byType: Record<string, number>;
    errors: number;
    oldest: Date | null;
  } {
    const stats = {
      total: this.subscriptions.size,
      byType: { postgres: 0, presence: 0, broadcast: 0 },
      errors: 0,
      oldest: null as Date | null
    };

    for (const [, subscription] of this.subscriptions) {
      stats.byType[subscription.type]++;
      if (subscription.errorCount > 0) {
        stats.errors++;
      }
      if (!stats.oldest || subscription.createdAt < stats.oldest) {
        stats.oldest = subscription.createdAt;
      }
    }

    return stats;
  }

  /**
   * Cleanup on destroy
   */
  destroy(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }
    this.unsubscribeAll();
    SubscriptionManager.instance = null;
  }
}

// Export singleton instance with lazy initialization to avoid TDZ errors
let _subscriptionManager: SubscriptionManager | null = null;
const getSubscriptionManager = (): SubscriptionManager => {
  if (!_subscriptionManager) {
    _subscriptionManager = SubscriptionManager.getInstance();
  }
  return _subscriptionManager;
};

// Use Proxy to forward all property access and method calls to the instance
export const subscriptionManager = new Proxy({} as SubscriptionManager, {
  get(_target, prop) {
    const instance = getSubscriptionManager();
    const value = (instance as any)[prop];
    // If it's a function, bind it to the instance
    if (typeof value === 'function') {
      return value.bind(instance);
    }
    return value;
  },
  set(_target, prop, value) {
    const instance = getSubscriptionManager();
    (instance as any)[prop] = value;
    return true;
  },
  has(_target, prop) {
    const instance = getSubscriptionManager();
    return prop in instance;
  },
});
